import PostInfoDate from "./PostInfoComponents/PostInfoDate";
import PostInfoPermalink from "./PostInfoComponents/PostInfoPermalink";
import PostInfoShopLink from "./PostInfoComponents/PostInfoShopLink";
import { PostInfoTaggedLink } from "./PostInfoComponents/PostInfoTaggedLink";
import PostInfoExternalLink from "./PostInfoComponents/PostInfoExternalLink";
import { useDesign } from "../../../context/DesignContext";
import { isHexDark } from "../../../Utils/HelperFunction/HelperFunction";
import { usePostClickTracking } from "./PostClickTrackingContext";
import {
  isSocialProfileUrl,
  resolvePostShopLinkOnly,
} from "../../../Utils/normalizeShopUrl";

const PostInfo = ({
  user_id,
  showAsAd,
  post_date,
  permalink,
  shop_link,
  post_id,
  media_url,
  handleRedirect,
  handleShop,
  handleTagged,
  username,
  shoppableContent,
  tagged_link,
  isGlobalView,
  platform,
  external_link,
  name,
  isAffiliateConnected,
  isVideo,
  overrideDesignTokens,
}: any) => {
  const {
    fontFamily,
    primaryFont,
    secondaryFont,
    primaryColor,
    secondaryColor,
    tertiaryColor,
    templateName,
    forthColor,
    socialIconColor: contextSocialIconColor,
  } = useDesign();
    const designTokens = {
      fontFamily,
      primaryFontFamily: overrideDesignTokens?.primaryFontFamily ?? primaryFont,
      secondaryFontFamily: overrideDesignTokens?.secondaryFontFamily ?? secondaryFont,
      primaryColor: overrideDesignTokens?.primaryColor ?? primaryColor,
      secondaryColor: overrideDesignTokens?.secondaryColor ?? secondaryColor,
      tertiaryColor: overrideDesignTokens?.tertiaryColor ?? tertiaryColor,
      text_color: overrideDesignTokens?.text_color,
      templateName: overrideDesignTokens?.templateName ?? templateName,
      forthColor : overrideDesignTokens?.forthColor ?? forthColor,
    };

  // Surfaces that need context-aware tracking (e.g. the Website Template search
  // page, which renders FeedOptimised directly and must stamp `page_key`) provide
  // overrides here. When absent, the component's own prop handlers are used.
  const postClickTracking = usePostClickTracking();
  const resolvedHandleRedirect = postClickTracking?.handleRedirect ?? handleRedirect;
  const resolvedHandleShop = postClickTracking?.handleShop ?? handleShop;
  const resolvedHandleTagged = postClickTracking?.handleTagged ?? handleTagged;
  // True shop_link only — do not promote mention tagged_link into the Shop icon.
  const shopUrl = resolvePostShopLinkOnly({
    shop_link,
    post_id,
    tagged_link,
    external_link,
  });
  const externalUrl =
    external_link && !isSocialProfileUrl(external_link) ? external_link : "";
  // @ mention chip can still show profile handles — that is intentional here.

  // Platform icons must contrast with the page background. Prefer the explicit
  // social-icon token; never use a washed-out `text_color` (often button text
  // like #e6e7eb on white embed pages) that matches the background luminance.
  const pageBg = String(
    overrideDesignTokens?.primaryColor ?? primaryColor ?? "#ffffff",
  ).trim() || "#ffffff";
  const pickContrastingIconColor = (
    ...candidates: Array<string | null | undefined>
  ) => {
    const bgDark = isHexDark(pageBg);
    for (const raw of candidates) {
      const c = String(raw || "").trim();
      if (!c) continue;
      if (isHexDark(c) === bgDark) continue;
      return c;
    }
    return bgDark ? "#ffffff" : "#000000";
  };
  const socialIconColor = pickContrastingIconColor(
    overrideDesignTokens?.social_icon_color,
    overrideDesignTokens?.socialIconColor,
    contextSocialIconColor,
    overrideDesignTokens?.text_color,
    overrideDesignTokens?.secondaryColor,
  );

  const renderLinks = () => (
    <div className="creator-feed-post-links">
      {shopUrl && (
        <PostInfoShopLink
          shop_link={shopUrl}
          shoppableContent={shoppableContent}
          post_id={post_id}
          media_url={media_url}
          handleShop={resolvedHandleShop}
          platform={platform}
          name={name}
          user_id={user_id}
          socialIconColor={socialIconColor}
          designTokens={designTokens}
        />
      )}

      {externalUrl && (
        <PostInfoExternalLink
          external_link={externalUrl}
          handleRedirect={resolvedHandleRedirect}
          post_id={post_id}
          media_url={media_url}
          platform={platform}
          name={name}
          user_id={user_id}
          socialIconColor={socialIconColor}
          designTokens={designTokens}
        />
      )}

      {tagged_link && (
        <PostInfoTaggedLink
          tagged_link={tagged_link}
          post_id={post_id}
          media_url={media_url}
          handleTagged={resolvedHandleTagged}
          platform={platform}
          name={name}
          user_id={user_id}
          socialIconColor={socialIconColor}
          designTokens={designTokens}
        />
      )}

      {
        <PostInfoPermalink
          permalink={permalink}
          handleRedirect={resolvedHandleRedirect}
          post_id={post_id}
          media_url={media_url}
          platform={platform}
          name={name}
          user_id={user_id}
          shop_link={shopUrl}
          socialIconColor={socialIconColor}
          designTokens={designTokens}
        />
      }

      {isGlobalView && username && (
        <p className="creator-feed-username">@{username}</p>
      )}
    </div>
  );

  const renderDate = (post_date: string) => {
    if (platform == "LTK" || platform == "Amazon" || platform == "ShopMy") {
      return isAffiliateConnected ? post_date : "";
    }
    return post_date;
  };

  return (
    <div className="creator-feed-content-info" style={{...(designTokens?.secondaryFontFamily && {fontFamily:designTokens?.secondaryFontFamily})}}>
      {!showAsAd ? (
        <>
          <PostInfoDate
            post_date={renderDate(post_date)}
            designTokens={designTokens}
          />
          {renderLinks()}
        </>
      ) : (
        <>
          <p className="creator-feed-content-date" style={{ color: isHexDark(primaryColor) ? "#ffffff" : "#000000" }}>Sponsored</p>
          {renderLinks()}
        </>
      )}
    </div>
  );
};

export default PostInfo;
